// Cross-tab cache sync via BroadcastChannel.
//
// Each browser tab owns its own QueryClient. Without a signaling layer, a
// mutation in tab A only invalidates tab A's cache. We use BroadcastChannel
// to post `{ type: "invalidate", queryKeys }` after every successful mutation;
// other tabs subscribe and call invalidateQueries locally - so they refetch
// on next render or window focus, whichever comes first.
//
// BroadcastChannel does not deliver a sender's own messages, so there's no
// risk of an invalidation loop within a single tab.

import { useEffect, useRef } from "react";

const CHANNEL_NAME = "popupstore-cache-sync";

export function createCacheSyncChannel() {
  if (
    typeof window === "undefined" ||
    typeof BroadcastChannel === "undefined"
  ) {
    return null;
  }
  return new BroadcastChannel(CHANNEL_NAME);
}

export function postCacheInvalidation(channel, queryKeys) {
  if (!channel || !queryKeys || queryKeys.length === 0) return;
  channel.postMessage({ type: "invalidate", queryKeys });
}

// Subscribe a handler to cross-tab invalidations of a specific queryKey.
//
// Use this when a component owns its fetch but still needs to react to cache
// invalidations from another tab.
//
// Matches a broadcasted key when its prefix equals `queryKey` - so a
// listener on `["quotes"]` fires for both `["quotes"]` and `["quotes", id]`
// invalidations. Same hierarchy convention as TanStack Query.
export function useCacheSyncListener(queryKey, handler) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const keyString = JSON.stringify(queryKey);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof BroadcastChannel === "undefined"
    ) {
      return undefined;
    }
    const target = JSON.parse(keyString);
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event) => {
      if (event.data?.type !== "invalidate") return;
      const { queryKeys } = event.data;
      if (!Array.isArray(queryKeys)) return;
      const match = queryKeys.some(
        (key) =>
          Array.isArray(key) &&
          key.length >= target.length &&
          target.every((part, i) => part === key[i])
      );
      if (match) handlerRef.current();
    };
    return () => channel.close();
  }, [keyString]);
}
